import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ago, formatBytes, useApi, usePoll, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

interface Issue { code: string; severity: 'info' | 'warning' | 'blocked'; message: string }
interface Preflight {
  targets: { screenId: string; screenName: string; timezone: string; deviceId: string | null; status: 'ok' | 'warning' | 'blocked'; issues: Issue[]; totalBytes: number }[];
  summary: { screens: number; ok: number; warnings: number; blocked: number; withoutDevice: number; totalBytes: number; timezones: string[] };
}

/** Focused publish flow: choose targets → review preflight → choose activation/rollout → publish → watch. */
export function Publish() {
  const org = useOrg();
  const nav = useNavigate();
  const toast = useToast();
  const locations = useApi<{ locations: { id: string; name: string; screenCount: number }[] }>(orgPath(org, '/locations'), [org.id]);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [pre, setPre] = useState<Preflight | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiFailure | null>(null);
  const [name, setName] = useState(`Update ${new Date().toLocaleDateString()}`);
  const [activation, setActivation] = useState<'immediate' | 'scheduled'>('immediate');
  const [notBefore, setNotBefore] = useState('');
  const [strategy, setStrategy] = useState<'all' | 'canary'>('all');
  const [exclude, setExclude] = useState(false);
  const idem = useMemo(() => crypto.randomUUID(), [pre]); // eslint-disable-line react-hooks/exhaustive-deps

  async function review() {
    setBusy(true);
    setErr(null);
    try {
      setPre(await api<Preflight>('POST', orgPath(org, '/publications/preflight'), { target: { locationIds } }));
    } catch (e) {
      setErr(e as ApiFailure);
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    setBusy(true);
    try {
      const r = await api<{ publicationId: string }>('POST', orgPath(org, '/publications'), {
        name, idempotencyKey: idem, target: { locationIds }, excludeIncompatible: exclude,
        activation: { mode: activation, notBefore: activation === 'scheduled' && notBefore ? new Date(notBefore).toISOString() : null },
        rollout: { strategy, canaryCount: 1, stageSize: 10, failureThresholdPct: 20 },
      });
      toast('Published. Watching rollout…');
      nav(`/publications/${r.publicationId}`);
    } catch (e) {
      setErr(e as ApiFailure);
    } finally {
      setBusy(false);
    }
  }
  const blocked = pre?.summary.blocked ?? 0;
  return (
    <>
      <div className="page-head">
        <div><h1>Publish</h1><p>Send the current playlists, schedules and music to screens as a signed, immutable release.</p></div>
        <Link to="/publications" className="btn">History</Link>
      </div>
      <div className="card card-pad stack" style={{ gap: 16 }}>
        <h2>1. Where</h2>
        {!locations.data ? <Loading rows={1} /> : (
          <div className="row">
            {locations.data.locations.map((l) => (
              <label key={l.id} className="row card" style={{ padding: '10px 14px', gap: 8 }}>
                <input type="checkbox" checked={locationIds.includes(l.id)} onChange={(e) => { setPre(null); setLocationIds(e.target.checked ? [...locationIds, l.id] : locationIds.filter((x) => x !== l.id)); }} />
                {l.name} <span className="subtle">{l.screenCount} screens</span>
              </label>
            ))}
          </div>
        )}
        <p className="subtle">Targets are a snapshot: screens paired later are not included until you publish again.</p>
        <div><button className="btn btn-primary" disabled={!locationIds.length || busy} onClick={() => void review()}>Review</button></div>
      </div>
      {err && <div style={{ marginTop: 16 }}><ErrorState error={err} /></div>}
      {pre && (
        <Sheet title="Review publication" subtitle={`${pre.summary.screens} screens · ${formatBytes(pre.summary.totalBytes)} to download · ${pre.summary.timezones.join(', ')}`} onClose={() => setPre(null)} wide
          footer={<>
            <button className="btn" onClick={() => setPre(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || !can(org, 'publish.create') || (blocked > 0 && !exclude) || blocked === pre.summary.screens} onClick={() => void publish()}>
              Publish to {pre.summary.screens - (exclude ? blocked : 0)} screen(s)
            </button>
          </>}>
          <div className="stack" style={{ gap: 16 }}>
            <div className="grid grid-stats">
              <div className="card stat"><div className="value" style={{ color: 'var(--ok)' }}>{pre.summary.ok}</div><div className="label">Ready</div></div>
              <div className="card stat"><div className="value" style={{ color: 'var(--warn)' }}>{pre.summary.warnings}</div><div className="label">With warnings</div></div>
              <div className="card stat"><div className="value" style={{ color: 'var(--danger)' }}>{pre.summary.blocked}</div><div className="label">Blocked</div></div>
              <div className="card stat"><div className="value">{pre.summary.withoutDevice}</div><div className="label">No player yet</div></div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Screen</th><th>Compatibility</th><th>Download</th><th>Notes</th></tr></thead>
                <tbody>
                  {pre.targets.map((t) => (
                    <tr key={t.screenId}>
                      <td>{t.screenName}<div className="subtle">{t.timezone}</div></td>
                      <td><Status value={t.status === 'ok' ? 'ready' : t.status} /></td>
                      <td>{formatBytes(t.totalBytes)}</td>
                      <td className="subtle">{t.issues.filter((i) => i.severity !== 'info').map((i) => <div key={i.code + i.message}>{i.severity === 'blocked' ? '⛔ ' : '⚠ '}{i.message}</div>)}{!t.issues.length && 'No issues'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {blocked > 0 && <label className="row"><input type="checkbox" checked={exclude} onChange={(e) => setExclude(e.target.checked)} /> Exclude the {blocked} blocked screen(s) and publish to the rest (they keep their current content)</label>}
            <div className="field"><label htmlFor="pub-name">Name</label><input id="pub-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="row">
              <div className="segmented" role="group" aria-label="Activation">
                <button aria-pressed={activation === 'immediate'} onClick={() => setActivation('immediate')}>Activate when downloaded</button>
                <button aria-pressed={activation === 'scheduled'} onClick={() => setActivation('scheduled')}>Activate at a time</button>
              </div>
              {activation === 'scheduled' && <input className="input" style={{ width: 'auto' }} type="datetime-local" aria-label="Activate at" value={notBefore} onChange={(e) => setNotBefore(e.target.value)} />}
            </div>
            <div className="segmented" role="group" aria-label="Rollout">
              <button aria-pressed={strategy === 'all'} onClick={() => setStrategy('all')}>All screens at once</button>
              <button aria-pressed={strategy === 'canary'} onClick={() => setStrategy('canary')}>Canary first, then stages of 10</button>
            </div>
            <p className="subtle">Offline players receive this release when they reconnect. Urgent changes cannot reach a device that is offline.</p>
          </div>
        </Sheet>
      )}
    </>
  );
}

interface PubDetail {
  publication: { id: string; name: string; status: string; createdAt: string; rollout: { strategy: string }; rollbackOf: string | null; activationMode: string };
  targets: { id: string; screenId: string; screenName: string; locationName: string; status: string; stage: number; sequence: number | null; reason: string | null; errorCode: string | null; bytesDownloaded: number | null; bytesTotal: number | null; updatedAt: string; connectivity: string }[];
}

export function Publications() {
  const org = useOrg();
  const { publicationId } = useParams();
  const list = useApi<{ publications: { id: string; name: string; status: string; createdAt: string; createdByEmail: string | null; counts: Record<string, number> | null; rollbackOf: string | null }[] }>(orgPath(org, '/publications'), [org.id]);
  if (publicationId) return <PublicationDetail id={publicationId} />;
  return (
    <>
      <div className="page-head"><div><h1>Publications</h1><p>Every release is immutable and signed. Roll back by publishing the previous content as a new release.</p></div><Link to="/publish" className="btn btn-primary"><Icon name="publish" /> Publish</Link></div>
      {list.error && <ErrorState error={list.error} />}
      {!list.data ? <Loading /> : !list.data.publications.length ? <div className="card"><Empty title="Nothing published yet" /></div> : (
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Publication</th><th>Status</th><th>Screens</th><th>By</th><th>When</th></tr></thead>
            <tbody>
              {list.data.publications.map((p) => (
                <tr key={p.id}>
                  <td><Link to={`/publications/${p.id}`}>{p.name}</Link>{p.rollbackOf && <span className="badge plain" style={{ marginLeft: 8 }}>rollback</span>}</td>
                  <td><Status value={p.status} /></td>
                  <td className="subtle">{Object.entries(p.counts ?? {}).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}</td>
                  <td className="subtle">{p.createdByEmail ?? 'system'}</td>
                  <td className="subtle">{ago(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PublicationDetail({ id }: { id: string }) {
  const org = useOrg();
  const toast = useToast();
  const nav = useNavigate();
  const { data, error, reload } = useApi<PubDetail>(orgPath(org, `/publications/${id}`), [id]);
  const live = !!data && ['queued', 'downloading', 'ready', 'partial'].includes(data.publication.status);
  usePoll(useCallback(() => void reload(), [reload]), 3000, live);
  const [confirm, setConfirm] = useState(false);
  async function rollback() {
    try {
      const r = await api<{ publicationId: string; skippedScreens: string[] }>('POST', orgPath(org, `/publications/${id}/rollback`), {});
      toast(`Rollback published as a new release${r.skippedScreens.length ? `; ${r.skippedScreens.length} screen(s) had no earlier release` : ''}`);
      setConfirm(false);
      nav(`/publications/${r.publicationId}`);
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  if (error) return <ErrorState error={error} />;
  if (!data) return <Loading />;
  const p = data.publication;
  const counts = data.targets.reduce<Record<string, number>>((m, t) => ((m[t.status] = (m[t.status] ?? 0) + 1), m), {});
  return (
    <>
      <div className="page-head">
        <div>
          <div className="breadcrumb"><Link to="/publications">Publications</Link> / {p.name}</div>
          <h1>{p.name}</h1>
          <p>{p.rollout.strategy === 'canary' ? 'Canary rollout' : 'All at once'} · {p.activationMode === 'scheduled' ? 'scheduled activation' : 'activates when downloaded'} · {ago(p.createdAt)}</p>
        </div>
        <div className="row">
          <Status value={p.status} />
          {can(org, 'publish.rollback') && p.status !== 'rolled_back' && <button className="btn" onClick={() => setConfirm(true)}><Icon name="rollback" /> Roll back</button>}
        </div>
      </div>
      <div className="row" style={{ marginBottom: 16 }} aria-live="polite">
        {Object.entries(counts).map(([k, v]) => <Status key={k} value={k} label={`${v} ${k.replace('_', ' ')}`} />)}
        {live && <span className="subtle">Updating live…</span>}
      </div>
      <div className="card table-wrap">
        <table className="table">
          <thead><tr><th>Screen</th><th>Stage</th><th>Device state</th><th>Release</th><th>Download</th><th>Connection</th><th>Updated</th></tr></thead>
          <tbody>
            {data.targets.map((t) => (
              <tr key={t.id}>
                <td>{t.screenName}<div className="subtle">{t.locationName}</div></td>
                <td>{t.stage === 0 ? (p.rollout.strategy === 'canary' ? 'canary' : '—') : `stage ${t.stage}`}</td>
                <td><Status value={t.status} />{(t.reason || t.errorCode) && <div className="subtle">{t.errorCode} {t.reason}</div>}</td>
                <td className="mono">{t.sequence ? `#${t.sequence}` : '—'}</td>
                <td>{t.bytesTotal ? <div className="progress" style={{ width: 120 }} role="progressbar" aria-valuenow={Math.round(((t.bytesDownloaded ?? 0) / t.bytesTotal) * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Download progress"><span style={{ width: `${((t.bytesDownloaded ?? 0) / t.bytesTotal) * 100}%` }} /></div> : '—'}</td>
                <td><Status value={t.connectivity === 'unknown' ? 'unpaired' : t.connectivity} /></td>
                <td className="subtle">{ago(t.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="subtle" style={{ marginTop: 12 }}>“Active” means the player confirmed it switched to this release. Proof that content actually played comes from playback reports.</p>
      {confirm && (
        <Sheet title="Roll back this publication?" onClose={() => setConfirm(false)} footer={<><button className="btn" onClick={() => setConfirm(false)}>Cancel</button><button className="btn btn-primary" onClick={() => void rollback()}>Roll back</button></>}>
          <p>Each screen receives a new release (higher sequence number) containing the content it played before this publication.</p>
        </Sheet>
      )}
    </>
  );
}
